import { useAuth0 } from "@auth0/auth0-react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { AuthContext } from "contexts/Authorization/AuthContext";
import { IdleCheckerContext } from "contexts/IdleChecker/IdleCheckerContext";
import { INFORMATION } from "contexts/Informations/utils";
import { hasAccessToCurrentTenant } from "utilities/tenantAccess";
import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import { useContext } from "react";
import { setCustomLanguageData } from "reduxState/customLanguageData/customLanguageDataSlice";
import {
  clearInformations,
  pushInformation,
} from "reduxState/informations/informationsSlice";
import { setIsLoggedIn } from "reduxState/login/loginSlice";
import { selectTables, setTables } from "reduxState/tables/tablesSlice";
import { filterTable } from "state/tables/tableHelpers";
import { mutate } from "swr";
import { getEnvVariable } from "utilities/env";
import { getFromCache, writeToCache } from "utilities/localStorage";
import { useOkta } from "./authOKTA";
import { useRevalidateCache } from "./useRevalidateCache";

type DecodedToken = {
  exp: number;
};

enum StatusCodes {
  UNAUTHORIZED = 401,
  NOT_MODIFIED = 304,
  FORBIDDEN = 403,
}

export function useAuth() {
  const { tokens, setToken } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  let token = tokens.admin_id_token;
  let refresh_token = tokens.admin_refresh_token;
  const authHeader = { Authorization: `Bearer ${token}` };
  const hasToken = Boolean(token);
  const tables = useAppSelector(selectTables);

  const revalidateCache = useRevalidateCache();
  const { runIdleChecker, stopIdleChecker } = useContext(IdleCheckerContext);

  const { logout: logoutAuth0, isAuthenticated: isAuthenticatedWithAuth0 } =
    useAuth0();
  const { logout: logoutOkta } = useOkta();

  const isAuthenticatedWithMA = useIsAuthenticated();
  const { instance } = useMsal();

  const OKTAClientId = getEnvVariable("REACT_APP_OKTA_CLIENT_ID");
  const OKTADirectoryId = getEnvVariable("REACT_APP_OKTA_PROVIDER_APP_URL");

  const login = async (user: string, password: string, keepSigned: boolean) => {
    const response = await fetch(
      `${getEnvVariable("REACT_APP_API_URL")}/admin/login_check`,
      {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: user, password: password }),
      },
    );
    const data = await response.json();
    if (response.ok) {
      if (data.token) {
        setToken(data.token, keepSigned ? data.refresh_token : null);
        dispatch(setIsLoggedIn(true));
        runIdleChecker(true);
      } else {
        throw new Error("No token");
      }
    } else {
      // eslint-disable-next-line no-throw-literal
      throw { error: data };
    }
  };

  const loginWithAuth0 = async (accessToken: string) => {
    const response = await fetch(
      `${getEnvVariable("REACT_APP_API_URL")}/admin/auth0/login_check`,
      {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access_token: accessToken }),
      },
    );
    const data = await response.json();
    if (response.ok) {
      if (data.token) {
        setToken(data.token, data.refresh_token ? data.refresh_token : null);
        dispatch(setIsLoggedIn(true));
        runIdleChecker(true);
      } else {
        throw new Error("No token");
      }
    } else {
      // eslint-disable-next-line no-throw-literal
      throw { error: data };
    }
  };

  const loginWithMA = async (accessToken: string) => {
    const response = await fetch(
      `${getEnvVariable("REACT_APP_API_URL")}/admin/oidc/login_check`,
      {
        method: "post",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    const data = await response.json();
    if (response.ok) {
      if (data.token) {
        setToken(data.token, data.refresh_token ? data.refresh_token : null);
        dispatch(setIsLoggedIn(true));
        runIdleChecker(true);
      }
    }
  };

  const logout = async () => {
    if (OKTAClientId && OKTADirectoryId) {
      await logoutOkta();
    }
    dispatch(setTables(filterTable(tables)));
    mutate(() => true, undefined, false);
    setToken("", "");
    dispatch(setIsLoggedIn(false));
    dispatch(clearInformations());
    dispatch(setCustomLanguageData({ code: "" }));
    stopIdleChecker();
    if (isAuthenticatedWithAuth0) {
      logoutAuth0();
    } else if (isAuthenticatedWithMA) {
      instance.logoutRedirect();
    }
  };

  const refreshToken = async () => {
    const response = await fetch(
      `${getEnvVariable("REACT_APP_API_URL")}/token/refresh`,
      {
        body: JSON.stringify({ refresh_token: refresh_token }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();
    token = data.token;
    refresh_token = data.refresh_token;
    runIdleChecker();
    setToken(data.token, data.refresh_token);

    return data;
  };

  async function fetcher(
    input: RequestInfo,
    init?: RequestInit,
  ): Promise<Response | any> {
    const isInputString = typeof input === "string";
    const { value: cachedValue, lastModified } = isInputString
      ? getFromCache(input as string)
      : null;

    const isRefreshToken = tokens.admin_refresh_token;
    const decoded = token && jwtDecode(token);
    const expirationDate = decoded
      ? (jwtDecode(token) as DecodedToken).exp * 1000
      : undefined;

    // Refresh token 1 min before expire date
    const minuteInMs = 60000;

    const shouldRefreshToken =
      isRefreshToken && expirationDate
        ? dayjs().isAfter(dayjs(expirationDate - minuteInMs))
        : false;

    if (shouldRefreshToken) {
      const newTokens = await refreshToken();

      init = {
        ...init,
        headers: {
          ...init?.headers,
          Authorization: `Bearer ${newTokens.token}`,
          ...(lastModified ? { "If-Modified-Since": lastModified } : {}),
        },
      };
    } else {
      init = {
        ...init,
        headers: {
          ...init?.headers,
          ...authHeader,
          ...(lastModified ? { "If-Modified-Since": lastModified } : {}),
        },
      };
    }

    const request = new Request(input, init);

    let response = await fetch(request);

    if (response.status === StatusCodes.NOT_MODIFIED) {
      return cachedValue;
    }

    const method = request.method;

    const lastModifiedResponseHeader = response.headers.get("last-modified");
    const isCachable = lastModifiedResponseHeader && isInputString;

    if (response.status === StatusCodes.UNAUTHORIZED) {
      dispatch(setIsLoggedIn(false));
      logout();
      dispatch(setCustomLanguageData({ code: "" }));
    } else if (response.status === StatusCodes.FORBIDDEN) {
      // Only show toast if user has access to current tenant
      // This prevents showing during tenant switching or for invalid tenants
      if (hasAccessToCurrentTenant()) {
        dispatch(
          pushInformation({ ...INFORMATION.UNAUTHORIZED, enableClose: true }),
        );
      }

      return null;
    } else {
      const isOK = response.ok;
      // Mark user as logged only when api status === 200 and
      // API request was made to authorized route
      if (hasToken && isOK) {
        dispatch(setIsLoggedIn(true));
      }
      const isGET = method.toUpperCase() === "GET";
      const contentType = response.headers.get("content-type");
      const isJSON = contentType?.indexOf("application/json") !== -1;
      let responseJSON;
      if (isGET && isJSON) {
        responseJSON = await response.json();
      }
      const responseData = responseJSON || response;

      if (isOK && !isGET) {
        revalidateCache();
      }

      if (isOK && isCachable) {
        writeToCache(input as string, responseData, {
          lastModified: lastModifiedResponseHeader,
        });
      }

      if (!isOK && isGET) {
        const error: any = new Error();
        error.info = await responseData;
        throw error;
      }

      return responseData;
    }
  }

  function arrayFetcher(urlArr: string[]) {
    const f = (u: any) => fetcher(u);
    return Promise.all(urlArr.map(f));
  }

  return {
    arrayFetcher,
    fetcher,
    hasToken,
    login,
    loginWithAuth0,
    loginWithMA,
    logout,
    refresh_token,
    token,
  };
}
